/* ModuleLibraryCard's colour channel (C7, ADR-0092): a module `tint` or the
   deprecated raw-colour `accent`, never both. A tint reads its classes from
   lib/tints.ts, so the only hues here are the deprecated accent's. Only a
   card that opens (`onOpen` or `href`) takes these classes. */
import { MODULE_TINT_GROUP_BORDER, type ModuleTint } from "../../lib/tints.js";

/**
 * @deprecated A raw colour, not an identity: pass `tint` (a module tint from
 * lib/tints.ts). Each value still renders today's border and focus ring.
 * Removed once nothing passes `accent`.
 */
export type ModuleLibraryCardAccent = "cyan" | "teal" | "gold";

/** Module identity or the deprecated raw-colour accent: the type refuses the
 *  pair. With neither, the card keeps today's teal border and ring. */
export type ModuleLibraryCardColourProps =
  | {
      /** Module identity (lib/tints.ts): the hover and focus-within border
       *  and the `icon` tile. Keyboard focus is the standard focus-ring. No
       *  identity stripe. */
      readonly tint?: ModuleTint | undefined;
      /** @deprecated Use `tint`. Never with `tint`. */
      readonly accent?: undefined;
    }
  | {
      /** @deprecated Use `tint`. Renders today's cyan, teal or gold border
       *  and tinted focus-within ring. Never with `tint`. */
      readonly accent?: ModuleLibraryCardAccent | undefined;
      readonly tint?: undefined;
    };

const ACCENT_CLASS: Record<ModuleLibraryCardAccent, string> = {
  cyan: "group-hover:border-pb-cyan/50 group-focus-within:border-pb-cyan/50",
  teal: "group-hover:border-pb-teal/50 group-focus-within:border-pb-teal/50",
  gold: "group-hover:border-pb-gold/50 group-focus-within:border-pb-gold/50",
};

const FOCUS_RING_CLASS: Record<ModuleLibraryCardAccent, string> = {
  cyan: "focus-within:ring-pb-cyan",
  teal: "focus-within:ring-pb-teal",
  gold: "focus-within:ring-pb-gold",
};

/* The standard focus-ring (theme.css `focus-ring`: 2px --ring outline, 1px
   offset) on the card while its title target is focus-visible. High
   contrast's unlayered `*:focus-visible` rule would ring the title too, so
   the card drops it (`!`) and takes that rule's 3px / 2px weight: one ring,
   as lib/field-frame.ts FRAME_FOCUS_RING does for a field. */
const OPEN_FOCUS_RING =
  "has-[h3_:focus-visible]:outline-2 has-[h3_:focus-visible]:outline-offset-1 has-[h3_:focus-visible]:outline-ring hc:has-[h3_:focus-visible]:outline-[3px] hc:has-[h3_:focus-visible]:outline-offset-2 hc:[&_h3_:focus-visible]:outline-none!";

/** The wrapper of a card that opens: the press dip and the focus treatment.
 *  No tint keeps today's accent ring (teal when unset). */
export function openWrapperClasses(
  tint: ModuleTint | undefined,
  accent: ModuleLibraryCardAccent | undefined,
) {
  if (tint) return ["press", OPEN_FOCUS_RING];
  return ["press", "focus-within:ring-2", FOCUS_RING_CLASS[accent ?? "teal"]];
}

/** The Card of a card that opens: the hover paint and the border. */
export function openCardClasses(
  tint: ModuleTint | undefined,
  accent: ModuleLibraryCardAccent | undefined,
) {
  const hover = "transition-colors group-hover:pb-overlay-paint";
  if (tint) return [hover, MODULE_TINT_GROUP_BORDER[tint]];
  return [hover, ACCENT_CLASS[accent ?? "teal"]];
}
